import { z } from "zod";
import { emailSchema } from "./schemas.js";
/**
 * CREDITS model — the customer currency (migration 0031, prototype-validated 2026-07-28).
 * Every billable action costs credit UNITS; money is exchanged only when buying credits.
 * These constants are the single source of truth for both API charge paths and web previews.
 */
/** What actions cost, in credit units. Owner-overridable via platform_settings 'credit_costs'. */
export const CREDIT_COSTS = { scan: 1, interview: 10 };
export const BILLING_CURRENCIES = ["INR", "USD", "GBP", "EUR"];
export const billingCurrencySchema = z.enum(BILLING_CURRENCIES);
/** Currency presentation: symbol + the minor-unit multiplier the gateway charges in. */
export const CURRENCY_META = {
    INR: { symbol: "₹", minor: 100, locale: "en-IN" },
    USD: { symbol: "$", minor: 100, locale: "en-US" },
    GBP: { symbol: "£", minor: 100, locale: "en-GB" },
    EUR: { symbol: "€", minor: 100, locale: "de-DE" },
};
/**
 * Rough FX to INR for OWNER-CONSOLE rollups only (revenue across currencies on one axis).
 * Never used to price or charge a customer — each org pays its own currency's price book.
 */
export const FX_TO_INR = { INR: 1, USD: 88, GBP: 112, EUR: 96 };
/** Gateway routing by currency — phase 1: Razorpay for all four (International handles the
 *  foreign cards). A later EUR→stripe cutover is a change HERE, not in product code. */
export const GATEWAY_BY_CURRENCY = {
    INR: "razorpay",
    USD: "razorpay",
    GBP: "razorpay",
    EUR: "razorpay",
};
/** Price of `credits` credits in a currency's MINOR units, given the per-credit anchor and a
 *  percentage discount. Rounded to the nearest minor unit — the one rounding rule everywhere. */
export function creditPriceMinor(credits, perCredit, discountPct, cur) {
    const gross = credits * perCredit * (1 - discountPct / 100);
    return Math.round(gross * CURRENCY_META[cur].minor);
}
/**
 * Render an exact minor-unit amount. The ONE money formatter — a price is never rounded for
 * display, because a rounded display is a different price. The owner console used to preview a
 * pack with Math.round() over MAJOR units while the gateway charged the exact minor amount, so a
 * 1-credit pack at 89% off advertised "₹8" and billed ₹7.77; the recruiter's pack card rounded
 * the same way above ₹10. Whole amounts drop the ".00" — that is the same number, not a
 * different one.
 */
export function formatMinor(minor, cur) {
    const meta = CURRENCY_META[cur];
    const exact = minor % meta.minor === 0;
    return (meta.symbol +
        (minor / meta.minor).toLocaleString(meta.locale, {
            minimumFractionDigits: exact ? 0 : 2,
            maximumFractionDigits: 2,
        }));
}
/** What a buyer will actually be charged for a pack/tier, as a string. Owner preview, recruiter
 *  pack card and any purchase confirm must all call THIS — one function, one number. */
export function creditPriceLabel(credits, perCredit, discountPct, cur) {
    return formatMinor(creditPriceMinor(credits, perCredit, discountPct, cur), cur);
}
/** Sourcing package floor (Mode A): 40 screens + 20 interviews + exactly 3 finalists. */
export const SOURCING_MIN = { screens: 40, interviews: 20, finalists: 3 };
export function sourcingPackageCredits(screens, interviews) {
    return screens * CREDIT_COSTS.scan + interviews * CREDIT_COSTS.interview;
}
export const packPurchaseSchema = z.object({
    packId: z.string().uuid(),
});
export const tierSubscribeSchema = z.object({
    tierId: z.string().uuid(),
});
export const budgetSetSchema = z.object({
    userId: z.string().uuid(),
    /** null clears the cap back to the shared pool */
    monthlyCredits: z.number().int().min(1).max(100000).nullable(),
});
export const creditRequestSchema = z.object({
    credits: z.number().int().min(1).max(100000),
    kind: z.enum(["pool", "budget"]),
    note: z.string().max(400).optional(),
});
export const engagementStageSchema = z.enum([
    "new", "intake", "posting", "sourcing", "interviewing", "shortlist", "closed",
]);
export const engagementDetailsSchema = z.object({
    salaryMin: z.string().max(40).optional(),
    salaryMax: z.string().max(40).optional(),
    location: z.string().max(120).optional(),
    workMode: z.enum(["Onsite", "Hybrid", "Remote"]).optional(),
    notice: z.string().max(60).optional(),
    skills: z.string().max(400).optional(),
    benefits: z.array(z.string().max(60)).max(20).optional(),
    staffAug: z.boolean().optional(),
});
/** The production benefit presets (mirrors NewRequest.tsx BENEFIT_PRESETS). */
export const BENEFIT_PRESETS = [
    "Health insurance", "ESOPs / equity", "Remote-first", "Hybrid", "Flexible hours",
    "Annual bonus", "Relocation support", "Learning budget", "Paid time off", "Provident fund",
];
export const distributorCreateSchema = z.object({
    name: z.string().min(2).max(200),
    region: z.enum(["in-central", "us-east", "eu-west"]),
    currency: billingCurrencySchema,
    adminEmail: emailSchema,
    /** present + >0 marks the org as a distributor partner */
    distSharePct: z.number().min(0.5).max(50).optional(),
    /** attach this org under an existing distributor */
    parentOrgId: z.string().uuid().optional(),
});
/** Presence weighting when the owner switch is ON: overall = 95% answers + 5% presence. */
export const PRESENCE_WEIGHT = 0.05;
/** #43: strict mode doubles presence's share of the overall (10% vs the natural 5%). Still a
 *  minority share on purpose — answers are the product; presence contextualises them. The report
 *  must always print WHICH weight applied, or scores stop being comparable across requisitions. */
export const STRICT_PRESENCE_WEIGHT = 0.1;
export function applyPresence(capability, presence, enabled, weight = PRESENCE_WEIGHT) {
    if (!enabled || presence == null)
        return Math.round(capability);
    return Math.round(capability * (1 - weight) + presence * weight);
}
//# sourceMappingURL=creditsModel.js.map